import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Badge } from 'components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { blogTypesUpdate } from '../../../services/api';
import { errMsg } from '../generator/util';

const BLOCKS = [
  ['brief', 'What this post is for', 'Who reads it and what they want. Injected as {blog_type_brief}.'],
  ['section_plan', 'How to structure it', 'The shape of the post. Injected as {section_plan}.'],
  ['tone', 'Tone', 'Injected as {tone}.'],
  ['avoid', 'Things to avoid', 'Injected as {avoid}.'],
];

function TypeCard({ t, categories, onSaved }) {
  const [form, setForm] = useState(t);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(t);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await blogTypesUpdate({
        id: t.id, name: form.name, category_id: form.category_id, brief: form.brief, section_plan: form.section_plan,
        stats_mode: form.stats_mode, tone: form.tone, avoid: form.avoid, is_active: !!form.is_active, change_note: note || null,
      });
      onSaved(saved);
      setForm(saved);
      setNote('');
      toast.success(`${saved.name} saved (v${saved.version})`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-token-large border border-borderGrey bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 max-w-xs font-medium" />
        <Badge variant="outline">v{t.version}</Badge>
        <div className="flex items-center gap-2">
          <Label className="text-token-base">Stats</Label>
          <Select value={form.stats_mode} onValueChange={(v) => setForm({ ...form, stats_mode: v })}>
            <SelectTrigger className="h-8 w-[120px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="full">full — tables</SelectItem>
              <SelectItem value="light">light — prose</SelectItem>
              <SelectItem value="none">none</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-token-base">Category</Label>
          <Select value={form.category_id ? String(form.category_id) : '__none'} onValueChange={(v) => setForm({ ...form, category_id: v === '__none' ? null : parseInt(v, 10) })}>
            <SelectTrigger className="h-8 w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">—</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-token-base">Active</Label>
          <Select value={form.is_active ? 'yes' : 'no'} onValueChange={(v) => setForm({ ...form, is_active: v === 'yes' })}>
            <SelectTrigger className="h-8 w-[80px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {BLOCKS.map(([k, label, hint]) => (
          <div key={k}>
            <Label>{label}</Label>
            <p className="mb-1 text-[11px] text-muted-foreground">{hint}</p>
            <Textarea rows={k === 'section_plan' || k === 'brief' ? 4 : 2} value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="text-token-base" />
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Input placeholder="What changed? (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-8 max-w-sm" />
        <Button size="sm" disabled={!dirty || saving} onClick={save}>Save</Button>
        {dirty && <Button size="sm" variant="ghost" onClick={() => setForm(t)}>Discard</Button>}
      </div>
    </div>
  );
}

export default function BlogTypesTab({ types, categories, onSaved }) {
  return (
    <div className="space-y-3">
      <p className="text-token-base text-muted-foreground">
        These short blocks are what you normally edit. Each blog type feeds them into the prompts as {'{blog_type_brief}'}, {'{section_plan}'}, {'{stats_mode}'}, {'{tone}'} and {'{avoid}'} — the full templates (Templates tab) stay the same across types.
      </p>
      {types.map((t) => <TypeCard key={t.id} t={t} categories={categories} onSaved={onSaved} />)}
    </div>
  );
}
